<template>
  <!--
        This component utilizes a native select element, and customizes its styling with the help of appeareance:none.
        The native select element is a rather complex input with specific internal structure, employing multiple facets
        for semantics and accessibility, which otherwise need to be replaced with WAI-ARIA and role attributes.
    -->
  <div class="input"
       v-bind="$attrs">
    <div
      class="field"
      :class="[
        {
          isLabelAtop,
          hasLabel: label,
          isRequired: required,
          isDisabled: disabled,
        },
      ]"
    >
      <label :for="inputId" v-if="label">{{ label }}</label>
      <select
        ref="selectElement"
        :name="inputName"
        :id="inputId"
        class="select"
        @change="change"
        :disabled="disabled"
        :required="required"
        :class="{ isEmpty: !modelValue }"
      >
        <option
          value=""
          v-if="!prefill && !placeholder"
          hidden
          disabled
          selected
        ></option>
        <option value="" :label="placeholder" v-if="placeholder" hidden disabled selected>
          {{ placeholder }}
        </option>
        <option
          :value="option.id"
          v-for="option in options"
          :key="option.id"
          :selected="modelValue === option.id"
        >
          {{ option.title }}
        </option>
      </select>
      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" v-bind:svg-inline="''" v-bind:class="'icon'" v-bind:role="'presentation'" v-bind:focusable="'false'" v-bind:tabindex="'-1'"><path d="M7.41 8.58L12 13.17l4.59-4.59L18 10l-6 6-6-6 1.41-1.42z"/></svg>
    </div>
    <span v-if="message" class="error">{{ message }}</span>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import {IOption} from "@/components/elements/types/IOption";

export default defineComponent({
  props: {
    options: {
      type: Array as () => IOption[],
      required: true,
      default: () => {},
    },
    label: {
      type: String,
    },
    name: {
      type: String,
    },
    id: {
      type: String,
    },
    required: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    prefill: {
      type: String,
    },
    message: {
      type: String,
    },
    placeholder: {
      type: String,
    },
  },
  data() {
    return {
      isFocused: false,
      inputId: "",
      inputName: "",
      modelValue: "",
    };
  },
  created() {
    this.inputId =
      this.id ||
      "id" + window.crypto.getRandomValues(new Uint32Array(1)).toString();
    this.inputName = this.name || this.inputId;
    this.modelValue = this.prefill;
  },
  computed: {
    isLabelAtop(): boolean {
      return this.isFocused || !!this.modelValue || !!this.prefill || !!this.placeholder;
    },
    validity(): object {
      const inputElement = this.$refs.selectElement as HTMLFormElement;
      return inputElement.validity;
    },
  },
  methods: {
    change(event: { target: HTMLInputElement }) {
      const value = event.target.value;
      this.modelValue = value;
      this.$emit(
        "update:modelValue",
        this.options.find((opt) => opt.id === value)
      );
    },
  },
});
</script>

<style lang="scss" scoped>
.field {
  position: relative;
  label {
    position: absolute;
    top: 11px;
    left: 17px;
    transition: transform 0.2s ease;
    transform-origin: left center;
    pointer-events: none;
    white-space: nowrap;
    color: $color-primary-gray;
  }

  &.hasLabel {
    select {
      padding-top: 16px;
    }
    &.isLabelAtop {
      label {
        transform: translateY(-10px) scale(0.7);
      }
    }
  }

  .icon {
    pointer-events: none;
    position: absolute;
    top: 12px;
    right: 12px;
    outline: none;
    fill: black;
  }

  &.isDisabled {
    .icon {
      fill: $color-secondary-bluegray-50;
    }
    label {
      cursor: not-allowed;
      color: $color-secondary-bluegray-50;
    }
    select {
      cursor: not-allowed;
      color: $color-secondary-bluegray;
      border-color: $color-secondary-bluegray-50;
      &:hover {
        background-color: #f6f6f6;
      }
    }
  }

  &.isRequired {
    &::after {
      content: "";
      display: block;
      position: absolute;
      right: 11px;
      top: 9px;
      width: 5px;
      height: 5px;
      border-radius: 5px;
      background-color: $color-secondary-darkgray;
      pointer-events: none;
      transition: all 0.2s ease;
    }
  }

  &:hover:not([disabled]) {
    .icon {
      fill: $color-primary-blue;
    }
  }
}

.select {
  height: 48px;
  font: inherit;
  padding: 10px 40px 6px 15px;
  border: 1px solid $color-primary-gray;
  border-radius: 0;
  color: $color-secondary-darkgray;
  background-color: $background-gray;
  width: 100%;
  max-width: 100%;
  -moz-appearance: none;
  -webkit-appearance: none;
  appearance: none;

  &:hover:not([disabled]) {
    color: $color-primary-blue;
    border: 1px solid  $color-primary-blue;    
    outline: none;    
  }

  &:focus {
    color: $color-primary-blue !important;
    border: 2px solid  $color-primary-blue;
    outline: 2px $color-primary-blue;
  }    

  &::-ms-expand {
    display: none;
  }

  &.isEmpty {
    color: $color-secondary-darkgray;
  }

  option {
    font: inherit;
  }
}

.select:focus + .icon {
  fill: $color-primary-blue;
}

.error {
  font-weight: bold;
  color: $error-color;
}
</style>
